import {
    CalendarDaysIcon,
    PencilSquareIcon,
    PresentationChartLineIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { revealStyle, useStagedReveal, type WhatsNewSlide } from '@odo/shared-ui';
import React from 'react';

export type Slide = WhatsNewSlide;

/**
 * Release notes for the doctor dashboard's "What's new" modal.
 *
 * Kept to the few things a doctor will actually notice, two per slide,
 * rather than everything that shipped - a release this large turns into a
 * changelog otherwise, and a changelog gets dismissed unread. The version
 * number is deliberately absent: doctors do not track releases, so the
 * headline sells the features rather than the number they arrived in.
 */

interface Feature {
    icon: typeof CalendarDaysIcon;
    iconBg: string;
    iconColor: string;
    title: string;
    description: string;
}

const PRACTICE_FEATURES: Feature[] = [
    {
        icon: CalendarDaysIcon,
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        title: 'Appointments',
        description:
            "Your whole practice's schedule in one place, so you always know who is coming in next."
    },
    {
        icon: PencilSquareIcon,
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
        title: 'Notes',
        // Worth keeping accurate as well as warm: doctors only ever create
        // `visibility: 'external'` notes (hard-enforced server-side), so every
        // note they write is already visible to the ODO team. Copy must never
        // imply a private scratchpad.
        description:
            'Keep your chart notes right on the case. Your ODO team sees them and takes them into account.'
    }
];

const PATIENT_FEATURES: Feature[] = [
    {
        icon: Squares2X2Icon,
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        title: 'A new patient page',
        description:
            'Open a patient and see exactly where the case stands, and what needs your attention next.'
    },
    {
        icon: PresentationChartLineIcon,
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        title: 'Full scan history',
        description:
            'Every scan from the very first one, colour-coded so progress and setbacks stand out.'
    }
];

const ReleaseWelcome: React.FC = () => {
    const stage = useStagedReveal(3, 100, 350);

    return (
        <div className="flex flex-col items-center justify-center w-full h-full">
            <div
                className="flex items-center justify-center w-14 h-14 rounded-2xl bg-yellow-50 border border-yellow-200 transition-all duration-700 ease-out"
                style={revealStyle(stage >= 1)}
            >
                <SparklesIcon className="w-7 h-7 text-yellow-500" />
            </div>

            <h2
                className="text-4xl font-bold text-gray-900 tracking-tight mt-6 text-center transition-all duration-700 ease-out"
                style={revealStyle(stage >= 2, 24)}
            >
                Your dashboard has new features
            </h2>

            <p
                className="text-base text-gray-400 text-center mt-3 leading-relaxed transition-all duration-700 ease-out"
                style={revealStyle(stage >= 3)}
            >
                Check out what&apos;s new
            </p>
        </div>
    );
};

interface FeatureSlideProps {
    title: string;
    features: Feature[];
}

const FeatureSlide: React.FC<FeatureSlideProps> = ({ title, features }) => {
    // One step for the heading, then one per card.
    const stage = useStagedReveal(features.length + 1, 100, 200);

    return (
        <div className="flex flex-col items-center w-full max-w-3xl mx-auto px-8 overflow-y-auto scrollbar-hide">
            <h2
                className="text-4xl font-bold text-gray-900 tracking-tight text-center transition-all duration-700 ease-out"
                style={revealStyle(stage >= 1, 24)}
            >
                {title}
            </h2>

            <div className="flex gap-5 mt-10 pb-4 w-full">
                {features.map((feature, i) => (
                    <div
                        key={feature.title}
                        className="flex-1 flex flex-col items-center text-center p-6 rounded-2xl bg-gray-50 border border-gray-100 transition-all duration-700 ease-out"
                        style={revealStyle(stage >= i + 2, 24)}
                    >
                        <div
                            className={`flex items-center justify-center w-11 h-11 rounded-xl ${feature.iconBg}`}
                        >
                            <feature.icon className={`w-5 h-5 ${feature.iconColor}`} />
                        </div>
                        <span className="text-base font-semibold text-gray-900 mt-4">
                            {feature.title}
                        </span>
                        <p className="text-sm text-gray-400 leading-relaxed mt-2">
                            {feature.description}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const slides: Slide[] = [
    {
        id: 'welcome',
        label: 'Welcome',
        content: <ReleaseWelcome />
    },
    {
        id: 'practice',
        label: 'Your practice',
        content: <FeatureSlide title="Plan and keep track" features={PRACTICE_FEATURES} />
    },
    {
        id: 'patient',
        label: 'Your patients',
        content: <FeatureSlide title="Everything in one place" features={PATIENT_FEATURES} />
    }
];
